import { ReactNode, useCallback, useState, JSX } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';

import { useDynatrace } from '@src/hooks/use-dynatrace.hook';
import { ErrorPageLayout } from '@src/components/error/error-page-layout';
import { LoginRedirectContext } from '@src/contexts/login-redirect-context';
import { LoginRedirectModal } from '@src/components/login-redirect-modal';
import WindowService from '@src/services/window.service';
import { PhoneNumberLink } from '@src/components/phone-number-link';
import { chewyCarePlusSupport } from '@src/constants/phone-numbers';

const ErrorFallback = ({ error }: FallbackProps) => {
  const { addAction, reportError } = useDynatrace();
  reportError(error);
  addAction('Showing Error Boundary in Login Redirect');
  return (
    <ErrorPageLayout
      headerText="Oops! Something went wrong."
      bodyContent={
        <p className="hc-pet-insurance__text-body">
          If the error persists after refreshing the page, please call{' '}
          <PhoneNumberLink contact={chewyCarePlusSupport} /> to speak to a
          representative.
        </p>
      }
    />
  );
};

/**
 * Provides a function used to display the {@link LoginRedirectModal}. This is
 * done as a Context Provider in order to bring the {@link LoginRedirectModal}
 * to all pages.
 *
 * You should not use the {@link LoginRedirectContext} directly, as it is used
 * by {@link useFetch} to display the {@link LoginRedirectModal} if the user's
 * Login Session expired.
 */
export const LoginRedirectProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const { addAction } = useDynatrace();
  const [modalOpen, setModalOpen] = useState(false);

  const showLoginRedirectModal = useCallback(() => {
    setModalOpen(true);
  }, [setModalOpen]);

  return (
    <LoginRedirectContext.Provider value={showLoginRedirectModal}>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        {children}
      </ErrorBoundary>
      <LoginRedirectModal
        open={modalOpen}
        onOpen={() => {
          addAction('Viewed Login Redirect Modal');
        }}
        onClose={() => {
          addAction('Closed Login Redirect Modal');
          setModalOpen(false);
        }}
        onContinue={() => {
          addAction('Clicked Sign in on Login Redirect Modal');

          WindowService.redirectTo(
            `/app/login?targetUrl=${encodeURIComponent(
              WindowService.window.location.pathname,
            )}`,
          );
        }}
      />
    </LoginRedirectContext.Provider>
  );
};
